<template>
  <div
    v-if="isPreOrder"
    class="pre-order-message"
    :class="{ 'pre-order-message--is-coming-soon': isPreOrderLaunch }"
  >
    <div class="pre-order-message__container">
      <icon-info />

      <span
        v-if="isPreOrderLaunch"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${launchMessage}`"
      />

      <span
        v-else-if="hasPreOrderTag && !product?.metafields?.preOrderAvailableDate"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${genericMessage} ${preOrderClosingMessage}`"
      />

      <span
        v-else-if="isPreOrderAvailable || isPreOrderClosing"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${preOrderAvailableMessage} ${preOrderClosingMessage}`"
      />
    </div>
  </div>

  <div v-else-if="hasAvailableToBuyDate">
    <div class="pre-order-message__container">
      <icon-info />

      <span
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${availableToBuyMessage}`"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Pre order message (pre-order-message)
 * -----------------------------------------------------------------------------
 * Show pre-order message.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 */
import { handleDate } from '~/helpers/general'
import IconInfo from '~icons/general/info.svg'

export default {
  name: 'PreOrderMessage',

  components: {
    IconInfo,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
    },
  },

  data() {
    return {

    }
  },

  computed: {

    /**
     * Is pre-order tag.
     */
    hasPreOrderTag() {
      return this.product.tags?.some((tag) => tag.includes('Pre-Order'))
    },

    /**
     * Is pre-order.
     */
    isPreOrder() {
      if (
        (this.hasPreOrderTag &&
          handleDate('current-date', this.product?.metafields?.preOrderAvailableDate)) ||
        (this.hasPreOrderTag &&
          handleDate('past-date', this.product?.metafields?.preOrderAvailableDate))
      ) {
        // When available date passed, check if extended date is still valid
        if (this.isPreOrderAvailable) {
          return true
        }
        return false
      }

      return this.hasPreOrderTag
    },

    /**
     * Is pre-order available.
     */
    isPreOrderAvailable() {
      // When closing passed, add extra days to available date
      if (this.product?.metafields?.preOrderClosingDate && !this.isPreOrderClosing) {
        const extendedDate = new Date(this.product?.metafields?.preOrderAvailableDate)
        if (isNaN(extendedDate.getTime())) {
          return false
        }
        const daysToAdd = parseInt(this.$string('product.pre_order.order_date_plus_variable')) || 0
        if (!isNaN(daysToAdd) && daysToAdd > 0) {
          extendedDate.setDate(extendedDate.getDate() + daysToAdd)
        }
        return handleDate('future-date', extendedDate)
      }
      return handleDate('future-date', this.product?.metafields?.preOrderAvailableDate)
    },

    /**
     * Is pre-order closing.
     */
    isPreOrderClosing() {
      return handleDate('future-date', this.product?.metafields?.preOrderClosingDate)
    },

    /**
     * Is pre-order launch.
     */
    isPreOrderLaunch() {
      return this.hasPreOrderTag &&
        this.product?.metafields?.preOrderLaunchDate &&
        // eslint-disable-next-line max-len
        handleDate('future-time', this.product?.metafields?.preOrderLaunchDate)
    },

    /**
     * Is pre-order available message.
     */
    isPreOrderAvailableMessage() {
      return this.product?.metafields?.preOrderAvailableMessage
    },

    /**
     * Is pre-order closing message.
     */
    isPreOrderClosingMessage() {
      return this.product?.metafields?.preOrderClosingMessage
    },

    /**
     * Pre-order available message.
     */
    preOrderAvailableMessage() {
      if (this.isPreOrderAvailable && this.product?.metafields?.preOrderAvailableDate) {
        // When closing passed, use extended date
        const dateToUse = new Date(this.product?.metafields?.preOrderAvailableDate)
        if (isNaN(dateToUse.getTime())) {
          return ''
        }
        // Add extra days when closing date has passed
        if (this.product?.metafields?.preOrderClosingDate && !this.isPreOrderClosing) {
          const daysToAdd = parseInt(this.$string('product.pre_order.order_date_plus_variable'))
          if (!isNaN(daysToAdd) && daysToAdd > 0) {
            dateToUse.setDate(dateToUse.getDate() + daysToAdd)
          }
        }
        // Subtract days when closing date has NOT passed
        if (this.product?.metafields?.preOrderClosingDate && this.isPreOrderClosing) {
          const daysToSubtract = parseInt(this.$string('product.pre_order.ship_date_minus_variable')) || 0
          if (!isNaN(daysToSubtract) && daysToSubtract > 0) {
            const calculatedDate = new Date(dateToUse)
            calculatedDate.setDate(calculatedDate.getDate() - daysToSubtract)
            // Only use calculated date if it's not in the past
            if (!handleDate('past-date', calculatedDate)) {
              dateToUse.setTime(calculatedDate.getTime())
            }
          }
        }
        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', dateToUse)}</b>`

        if (this.isPreOrderAvailableMessage) {
          return `${this.isPreOrderAvailableMessage} ${formatDate}.`
        }

        return this.$string(
          'product.pre_order.generic_available_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      return ''
    },

    /**
     * Pre-order closing message.
     */
    preOrderClosingMessage() {
      if (this.isPreOrderClosing) {
        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderClosingDate))}</b>`

        if (this.isPreOrderClosingMessage) {
          return `${this.isPreOrderClosingMessage} ${formatDate}.`
        }

        return this.$string(
          'product.pre_order.generic_closing_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      if (this.product?.metafields?.preOrderClosingDate && !this.isPreOrderClosing) {
        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderClosingDate))}</b>`

        return this.$string(
          'product.pre_order.generic_closing_ended_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      return ''
    },

    /**
     * Launch message string.
     */
    launchMessage() {
      const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderLaunchDate))}</b>`
      const dateCurrent = new Date(this.product?.metafields?.preOrderLaunchDate)
      const hour = dateCurrent.getHours().toString().padStart(2, '0')
      const minute = dateCurrent.getMinutes().toString().padStart(2, '0')
      const formatTime = `<b>${hour}:${minute}</b>`

      return this.$string(
        'product.pre_order.generic_launch_message_date_html',
        {
          replace: {
            time: formatTime,
            date: formatDate,
          },
        },
      )
    },

    /**
     * Generic message string.
     */
    genericMessage() {
      return this.$string('product.pre_order.generic_default_message_html')
    },

    /**
     * Available to buy date.
     * - Check if 'availableToBuyDate' date is in the future.
     */
    hasAvailableToBuyDate() {
      return this.product?.metafields?.availableToBuyDate &&
        // eslint-disable-next-line max-len
        handleDate('future-time', this.product?.metafields?.availableToBuyDate) &&
        this.variant?.available
    },

    /**
     * Available to buy message.
     */
    availableToBuyMessage() {
      const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.availableToBuyDate))}</b>`
      const dateCurrent = new Date(this.product?.metafields?.availableToBuyDate)
      const hour = dateCurrent.getHours().toString().padStart(2, '0')
      const minute = dateCurrent.getMinutes().toString().padStart(2, '0')
      const formatTime = `<b>${hour}:${minute}</b>`

      return this.$string(
        'product.pre_order.generic_available_to_buy_message_date_html',
        {
          replace: {
            time: formatTime,
            date: formatDate,
          },
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import './pre-order-message';
</style>
